import React, { useState, useEffect } from 'react';
import { api, useLanguage, useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, TrendingUp, Leaf, Flame, Droplets, Wheat } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart
} from 'recharts';

const PERIODS = [
  { value: 'week', label: { fr: '7 jours', en: '7 days' } },
  { value: 'month', label: { fr: '30 jours', en: '30 days' } }
];

const MACRO_COLORS = {
  protein: '#D4FF00',      // Primary green-yellow
  calories: '#FF6B35',     // Orange
  carbs: '#3B82F6',        // Blue
  fats: '#EF4444',         // Red
  fiber: '#22C55E'         // Green
};

export const MacroProgressCharts = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMacro, setSelectedMacro] = useState('protein');

  useEffect(() => {
    if (user) {
      fetchMacroHistory();
    }
  }, [period, user]);

  const fetchMacroHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dashboard/macro-history?period=${period}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching macro history:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (period === 'week') {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
    } else if (period === 'month') {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric' });
    } else {
      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' });
    }
  };

  const macroOptions = [
    { key: 'protein', icon: Flame, label: { fr: 'Protéines', en: 'Protein' }, unit: 'g', color: MACRO_COLORS.protein },
    { key: 'calories', icon: TrendingUp, label: { fr: 'Calories', en: 'Calories' }, unit: '', color: MACRO_COLORS.calories },
    { key: 'carbs', icon: Wheat, label: { fr: 'Glucides', en: 'Carbs' }, unit: 'g', color: MACRO_COLORS.carbs },
    { key: 'fats', icon: Droplets, label: { fr: 'Lipides', en: 'Fats' }, unit: 'g', color: MACRO_COLORS.fats },
    { key: 'fiber', icon: Leaf, label: { fr: 'Fibres', en: 'Fiber' }, unit: 'g', color: MACRO_COLORS.fiber }
  ];

  const getDataKey = (macro) => {
    if (macro === 'protein') return 'protein_g';
    if (macro === 'calories') return 'calories';
    if (macro === 'carbs') return 'carbs_g';
    if (macro === 'fats') return 'fats_g';
    if (macro === 'fiber') return 'fiber_g';
    return 'protein_g';
  };

  const getTarget = (macro) => {
    if (!data?.targets) return null;
    if (macro === 'protein') return data.targets.protein_min_g;
    if (macro === 'calories') return data.targets.calories;
    if (macro === 'fiber') return data.targets.fiber_g;
    return null;
  };

  const selectedMacroOption = macroOptions.find(m => m.key === selectedMacro);
  const target = getTarget(selectedMacro);

  // Prepare chart data with formatted dates
  const chartData = data?.data?.map(d => ({
    ...d,
    displayDate: formatDate(d.date)
  })) || [];

  // For longer periods, sample data points to avoid overcrowding
  const sampledData = chartData;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            {language === 'fr' ? 'Progression' : 'Progress'}
          </CardTitle>
          
          {/* Period Selector */}
          <div className="flex gap-1 bg-secondary p-1 rounded-lg">
            {PERIODS.map(p => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? 'default' : 'ghost'}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1 text-xs font-heading uppercase ${
                  period === p.value 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label[language] || p.label.en}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Macro Selector */}
        <div className="flex flex-wrap gap-2">
          {macroOptions.map(macro => {
            const Icon = macro.icon;
            const isSelected = selectedMacro === macro.key;
            return (
              <Button
                key={macro.key}
                size="sm"
                variant={isSelected ? 'default' : 'outline'}
                onClick={() => setSelectedMacro(macro.key)}
                style={{ 
                  backgroundColor: isSelected ? macro.color : 'transparent',
                  borderColor: macro.color,
                  color: isSelected ? '#09090B' : macro.color
                }}
                className="font-heading uppercase text-xs"
                data-testid={`macro-${macro.key}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {macro.label[language] || macro.label.en}
              </Button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : (
          <>
            {/* Chart */}
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sampledData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`gradient-${selectedMacro}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={selectedMacroOption?.color} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={selectedMacroOption?.color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fill: '#A1A1AA', fontSize: 11 }}
                    axisLine={{ stroke: '#27272A' }}
                    tickLine={{ stroke: '#27272A' }}
                  />
                  <YAxis 
                    tick={{ fill: '#A1A1AA', fontSize: 11 }}
                    axisLine={{ stroke: '#27272A' }}
                    tickLine={{ stroke: '#27272A' }}
                    width={45}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                      color: '#FAFAFA'
                    }}
                    formatter={(value) => [
                      `${value}${selectedMacroOption?.unit}`, 
                      selectedMacroOption?.label[language] || selectedMacroOption?.label.en
                    ]}
                    labelFormatter={(label) => label}
                  />
                  {target && (
                    <ReferenceLine 
                      y={target} 
                      stroke={selectedMacroOption?.color} 
                      strokeDasharray="5 5"
                      strokeOpacity={0.7}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey={getDataKey(selectedMacro)}
                    stroke={selectedMacroOption?.color}
                    strokeWidth={2}
                    fill={`url(#gradient-${selectedMacro})`}
                    dot={period === '1w' ? { fill: selectedMacroOption?.color, r: 4 } : false}
                    activeDot={{ r: 6, fill: selectedMacroOption?.color }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-secondary p-3 rounded-lg text-center">
                <p className="text-xs text-muted-foreground uppercase">
                  {language === 'fr' ? 'Moyenne' : 'Average'}
                </p>
                <p className="font-heading text-lg" style={{ color: selectedMacroOption?.color }}>
                  {data?.averages?.[getDataKey(selectedMacro)] || 0}
                  <span className="text-sm">{selectedMacroOption?.unit}</span>
                </p>
              </div>
              
              {target && (
                <div className="bg-secondary p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground uppercase">
                    {language === 'fr' ? 'Objectif' : 'Target'}
                  </p>
                  <p className="font-heading text-lg text-foreground">
                    {target}
                    <span className="text-sm">{selectedMacroOption?.unit}</span>
                  </p>
                </div>
              )}
              
              <div className="bg-secondary p-3 rounded-lg text-center">
                <p className="text-xs text-muted-foreground uppercase">
                  {language === 'fr' ? 'Jours trackés' : 'Days tracked'}
                </p>
                <p className="font-heading text-lg text-foreground">
                  {data?.data?.filter(d => d.meals_count > 0).length || 0}
                  <span className="text-sm text-muted-foreground">/{data?.data?.length || 0}</span>
                </p>
              </div>
              
              <div className="bg-secondary p-3 rounded-lg text-center">
                <p className="text-xs text-muted-foreground uppercase">
                  {language === 'fr' ? 'Repas total' : 'Total meals'}
                </p>
                <p className="font-heading text-lg text-foreground">
                  {data?.data?.reduce((sum, d) => sum + d.meals_count, 0) || 0}
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MacroProgressCharts;
